import type { DeliveryStatus, ScheduledDelivery } from "../../api/types";
import { DeliveryCard } from "./DeliveryCard";

const STATUS_CHIPS: Array<{ key: DeliveryStatus | ""; label: string }> = [
  { key: "", label: "All" },
  { key: "SCHEDULED", label: "Scheduled" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "COMPLETED", label: "Completed" },
  { key: "CANCELED", label: "Canceled" },
];

/**
 * G-95 — the deliveries list, at every width.
 *
 * Built for the phone, in place of a table that could not be reached past its
 * third column: one column of cards under a row of status chips. The owner
 * preferred the cards on desktop too (2026-10-03), so the table is gone and the
 * chips are the only status filter. Nothing here scrolls sideways except the
 * chips.
 *
 * The chips carry no counts, and that is a deliberate departure from the
 * approved mockup: the status filter is a server query, so when one status is
 * chosen the rows for the others are not on the page to count. A count that
 * silently means "of what you are already looking at" is worse than no count.
 */
export function DeliveryCardList({
  rows,
  status,
  onStatusChange,
  confirmLabel,
  loading,
}: {
  rows: ScheduledDelivery[];
  /** Null for a florist, who has no status filter at either width. */
  status: DeliveryStatus | "" | null;
  onStatusChange: (next: DeliveryStatus | "") => void;
  confirmLabel: boolean;
  loading: boolean;
}) {
  return (
    <div className="dlv-cards">
      {status !== null ? (
        <div className="dlv-chips" role="group" aria-label="Status">
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.key || "all"}
              type="button"
              className="dlv-chip"
              aria-pressed={status === chip.key}
              onClick={() => onStatusChange(chip.key)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="muted dlv-empty">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted dlv-empty">
          {status ? "No deliveries with this status." : "No deliveries."}
        </p>
      ) : (
        <div className="dlv-card-list">
          {rows.map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              confirmLabel={confirmLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
}
